import React, { createContext, useContext, useMemo } from "react";
import { Box, Icons } from "@curves/ui-base";
import useInventoryNav from '@clubInventory/hooks/useInventoryNav';
import clsx from "clsx";

const InventoryNavContext = createContext(null);

const useInventoryNavContext = () => {
	const context = useContext(InventoryNavContext);
	if (!context) {
		throw new Error(
			"useInventoryNavContext 必須在 InventoryNav 中使用"
		);
	}
	return context;
};

/**
 * 選單觸發按鈕
 *
 * @param {Object} props
 * @param {boolean} [props.asChild=false] - 如果為 true，將 props 合併到子元素而非渲染 Button
 * @param {string} [props.className] - 自定義 className
 * @param {React.ReactNode} [props.children] - 子元素（當 asChild 為 true 時使用）
 */
const Trigger = ({ asChild = false, className, children, ...props }) => {
	const { toggle, isOpen } = useInventoryNavContext();

	if (asChild) {
		if (!React.isValidElement(children)) return null;

		return React.cloneElement(children, {
			onClick: (e) => {
				if (children.props.onClick) children.props.onClick(e);
				toggle();
			},
			className: clsx("inventory-nav-trigger", className, { "is-open": isOpen }, children.props.className),
			...props,
		});
	}

	// 預設渲染 Button
	return (
		<button
			className={clsx("inventory-nav-trigger", className, { "is-open": isOpen })}
			onClick={toggle}
			{...props}
		>
			{children || <Icons.Bars htmlColor="#fff" />}
		</button>
	);
};

/**
 * 選單面板，會根據選單狀態顯示或隱藏
 */
const Panel = ({ children, className, style, ...props }) => {
	const { isOpen, isExpanded, navWidth, animationDuration } = useInventoryNavContext();

	return (
		<Box
			className={clsx("inventory-nav-panel", className, {
				"is-open": isOpen,
				"is-expanded": isExpanded
			})}
			style={{
				width: navWidth,
				transform: isOpen ? "translateX(0)" : `translateX(-100%)`,
				transition: `transform ${animationDuration}ms ease-in-out`,
				...style, // 合併外部傳入的 style
			}}
			{...props}
		>
			{children}
		</Box>
	);
};

/**
 * 內容區域，會根據選單狀態調整寬度
 */
const Content = React.memo(({ children, className, ...props }) => {
	const { isOpen, isExpanded, navWidth, animationDuration } = useInventoryNavContext();
	return (
		<Box
			className={clsx("inventory-nav-content", className, {
				"nav-open": isOpen,
				"nav-expanded": isExpanded,
			})}
			style={{
				marginLeft: isOpen ? `${navWidth}px` : 0,
				transition: `margin-left ${animationDuration}ms ease-in-out`,
				width: '100%'
			}}
			{...props}
		>
			{children}
		</Box>
	);
});

/**
 * InventoryNav 主組件
 *
 * @param {Object} props
 * @param {React.ReactNode} props.children - 子組件
 * @param {boolean} [props.defaultOpen] - 預設是否展開
 * @param {number} [props.animationDuration] - 動畫時長
 * @param {number} [props.navWidth] - 選單寬度
 * @param {Function} [props.onOpen] - 選單開啟時的回調
 * @param {Function} [props.onClose] - 選單關閉時的回調
 * @param {Function} [props.onToggle] - 選單切換時的回調
 */
const InventoryNav = ({
	children,
	defaultOpen = false,
	animationDuration = 300,
	navWidth = 240,
	onOpen,
	onClose,
	onToggle,
}) => {
	const navState = useInventoryNav({
		defaultOpen,
		animationDuration,
		navWidth,
		onOpen,
		onClose,
		onToggle,
	});

	const contextValue = useMemo(() => navState,
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[
		navState.isOpen,
		navState.isExpanded,
		navState.navWidth,
		navState.toggle,
	])
	return (
		<InventoryNavContext.Provider value={contextValue}>
			{children}
		</InventoryNavContext.Provider>
	);
};

InventoryNav.Trigger = Trigger;
InventoryNav.Panel = Panel;
InventoryNav.Content = Content;

export default InventoryNav;
export { useInventoryNavContext };
